import { ColumnsModel, QueryBuilderComponent, RuleChangeEventArgs, RuleModel } from "@syncfusion/ej2-react-querybuilder";
import { Fragment, useEffect } from "react";
import { useAsync } from "react-async";
import { DropDownList } from '@syncfusion/ej2-dropdowns';
import { getProofingBuilderConfig } from "../../api/services/proofingRulesService";
import ProofingRuleBuilderConfig from "../../api/models/proofingRuleBuilderConfig";
import DOMPurify from "dompurify";

type Props = {
    defaultRule: RuleModel,
    scopeId: number,
    ruleChanged: (rule: RuleModel) => void;
};

let ActiveQueryBuilder: QueryBuilderComponent;
let ColumnConfig: ProofingRuleBuilderConfig;

class AggregateFunctionValueTemplate {
    options: string[];
    dropdown: DropDownList | undefined;
    activeInput: HTMLInputElement | undefined;
    value: string | undefined;

    constructor(options: string[]) {
        this.options = options;
    }

    create() {
        //console.log("Create()");
        let elm = document.createElement('input');
        elm.setAttribute('type', 'text');
        return elm;
    }

    destroy() {
        //console.log("Destroy()");
        this.value = undefined;
        if (typeof this.dropdown !== "undefined")
            this.dropdown.destroy();
    }

    write(args: { elements: any, values: string[] | string, operator: string }) {
        if (typeof args.values !== "undefined" && Array.isArray(args.values))
            args.values = args.values[0];

        //wire up change listener for the operator field
        //   for some reason this isn't happening like it should... ~tdepole
        let operatorId = args.elements.id.replace('valuekey0', 'operatorkey_hidden');
        let operatorInput = document.getElementById(operatorId);
        if (operatorInput) {
            operatorInput.onchange = (e) => {
                if (this.activeInput)
                    ActiveQueryBuilder.notifyChange(this.activeInput.value, this.activeInput);
            }
        }

        //set value
        //if (args.values !== "")
        this.value = args.values ? DOMPurify.sanitize(args.values[0]) as string : args.values[0];
        if (typeof this.value === "undefined") {
            this.value = this.options[0]!;
        }

        //console.log('Write()', { argValue: args.values, elementValue: args.elements?.value, templateValue: this.value });

        //determine if the user has a custom value entered and then render the right input

        if (this.options.some(o => o === this.value) || this.value === "") {
            this.bind_dropdown(args);
        } else {
            this.bind_textField(args);
        }
    }

    bind_dropdown(args: { elements: Element, values: string[] | string, operator: string }) {
        let ds: string[] = this.options.map(option => DOMPurify.sanitize(option));
        let dropDownObj = this.dropdown = new DropDownList({
            dataSource: ds,
            value: this.value ?? "Target",
            change: (e: any) => {
                //if the user didn't select custom we can notify the query builder of the change and stop here
                if (typeof e.itemData.value === "string" && e.itemData.value !== "Custom") {
                    this.activeInput = e.element;
                    this.value = e.itemData.value;
                    ActiveQueryBuilder.notifyChange(e.itemData.value, e.element);
                    return;
                }

                //if custom is selected then change the dropdown to text input
                this.dropdown?.destroy();

                this.bind_textField(args);
            }
        });
        dropDownObj.appendTo('#' + args.elements.id);
        this.activeInput = dropDownObj.element as HTMLInputElement; //needed for operator change notifications
    }

    bind_textField(args: { elements: Element, values: string[] | string, operator: string }) {
        let textInput = document.getElementById(args.elements.id);
        this.activeInput = textInput as HTMLInputElement; //needed for operator change notifications
        if (textInput) {
            textInput.setAttribute('type', 'number');
            textInput.setAttribute('style', 'width: 190px; border: none; border-bottom: 1px solid #919191; height: 30px;');
            textInput.setAttribute('value', this.value ?? "0");
            textInput.onchange = (e: Event) => {
                const target = e.target as HTMLInputElement
                this.value = target.value;
                ActiveQueryBuilder.notifyChange(target.value, target);
            }
        }
    }
}

const operators_list = [
    { key: "in", value: "in" },
    { key: "not in", value: "notin" }
]

const operators_bool = [
    { key: "is", value: "equals" },
    { key: "is not", value: "notequal" },
    { key: "greater than", value: "greaterthan" },
    { key: "less than", value: "lessthan" },
];

const ProofingRuleEditor = ({ defaultRule, ruleChanged, scopeId }: Props) => {
    //this will populate all the options that are available to users to define rules
    const generateColumns = (config: ProofingRuleBuilderConfig) => {
        let columns: ColumnsModel[] = [
            {
                field: "datapoint.division",
                label: "Division",
                type: "string",
                category: "Data Point",
                values: config?.divisions ?? [],
                operators: operators_list
            },
            {
                field: "datapoint.district",
                label: "District",
                type: "string",
                category: "Data Point",
                values: config?.districts ?? [],
                operators: operators_list
            },
            {
                field: "datapoint.program",
                label: "Programs",
                type: "string",
                category: "Data Point",
                values: config?.programs ?? [],
                operators: operators_list
            },
            {
                field: "datapoint.discipline",
                label: "Discipline",
                type: "string",
                category: "Data Point",
                values: config?.disciplines ?? [],
                operators: operators_list
            },
            {
                field: "datapoint.entrytype",
                label: "Entry Type",
                type: "string",
                category: "Data Point",
                values: config?.entryTypes ?? [],
                operators: operators_list
            },
            {
                field: "datapoint.tasktype",
                label: "Task Type",
                type: "string",
                category: "Data Point",
                values: config?.taskTypes ?? [],
                operators: operators_list
            },
            {
                field: "datapoint.grade",
                label: "Grade",
                type: "string",
                category: "Data Point",
                values: config?.grades ?? [],
                operators: operators_list
            },
            {
                field: "datapoint.status",
                label: "Student status",
                type: "string",
                category: "Data Point",
                values: config?.studentStatus ?? [],
                operators: operators_list
            }
        ];

        //Aggregate
        config?.aggregateFunctions.filter(f => f.scopeId === scopeId).forEach(func => {
            columns.push({
                field: "aggregate." + func.key,
                label: func.label,
                type: "string",
                category: "Aggregate",
                template: new AggregateFunctionValueTemplate([...config.cascadingVariables, "Custom"]),
                operators: operators_bool
            });
        });

        return columns;
    };

    //need to load all the options from the API to be used for rule columns
    useAsync({
        promiseFn: getProofingBuilderConfig,
        onResolve: (res) => {
            console.log("api response");
            console.log(res);
            if (typeof res.data !== "undefined") {
                ColumnConfig = res.data;

                 // Sanitize columns (divisions, districts, etc.)
            ColumnConfig.divisions = ColumnConfig.divisions.map(item => DOMPurify.sanitize(item));
            ColumnConfig.districts = ColumnConfig.districts.map(item => DOMPurify.sanitize(item));
            ColumnConfig.programs = ColumnConfig.programs.map(item => DOMPurify.sanitize(item));
            ColumnConfig.disciplines = ColumnConfig.disciplines.map(item => DOMPurify.sanitize(item));
            ColumnConfig.entryTypes = ColumnConfig.entryTypes.map(item => DOMPurify.sanitize(item));
            ColumnConfig.taskTypes = ColumnConfig.taskTypes.map(item => DOMPurify.sanitize(item));
            ColumnConfig.grades = ColumnConfig.grades.map(item => DOMPurify.sanitize(item));
            ColumnConfig.studentStatus = ColumnConfig.studentStatus.map(item => DOMPurify.sanitize(item));

            // Aggregate functions
            ColumnConfig.aggregateFunctions.forEach(func => {
                func.key = DOMPurify.sanitize(func.key); // sanitize the function key
                func.label = DOMPurify.sanitize(func.label); // sanitize the function label
            });
                ActiveQueryBuilder.columns = generateColumns(ColumnConfig);
                ActiveQueryBuilder.refresh();
            }
        }
    });

    //need to manually tell the QueryBuilderComponent to refresh when the view changes
    useEffect(() => {
        ActiveQueryBuilder.reset();
        ActiveQueryBuilder.setRules(defaultRule);

        if (typeof ColumnConfig !== "undefined") {
            ActiveQueryBuilder.columns = generateColumns(ColumnConfig);
            ActiveQueryBuilder.refresh();
        }
    });

    return (
        <Fragment>
            <QueryBuilderComponent
                className="mt-5"
                width="100%"
                rule={defaultRule}
                ruleChange={(e: RuleChangeEventArgs) => {
                    ruleChanged(e.rule);
                }}
                ref={(scope) => { if (scope != null) { ActiveQueryBuilder = scope } }}
            />
        </Fragment>
    )
}

export default ProofingRuleEditor;